// src/base/HouseKeepingRoutes.jsx
import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import PlanificationPage from "../planification/pages/planification";
import MainLayout from "./MainLayout";
import LogoutPage from "../authentication/logout";
import LoginPage from "../authentication/login";
import DashboardPage from "../dashboard/pages/dashboard";
import RulesPage from "../rules/pages/rules";
import SettingsPage from "../settings/pages/settings";
import PropertyPage from "../property/pages/property";
import PropertyCategoryPage from "../property/pages/propertyCategoryPage";
import BlocksPage from "../blocks/pages/blocks";
import { AuthProvider } from "./context/authcontext";
import { CommonProvider } from "./context/commonContext";
import ProtectedRoute from "../components/protectedRoute";
import { PropertyProvider } from "../property/propertyContext";
import { CategoryProvider } from "../property/categoryContext";
import { RuleProvider } from "../rules/ruleContext";
import { PlanificationProvider } from "../planification/planificationContext";
import { SettingsProvider } from "../settings/settingsContext";
import { InventoryProvider } from "../inventory/inventoryContext";
import { BlocksProvider } from "../blocks/blocksContext";
import InventoryPage from "../inventory/pages/inventory";
import AdminLayout from "./AdminLayout";
import { AdminPropertiesListProvider } from "../admin_properties/admin_properties_list/adminPropertiesListContext";
import AdminPropertiesListPage from "../admin_properties/admin_properties_list/pages/adminPropertiesListPage";
import { AdminUsersListProvider } from "../admin_users/admin_users_list/adminUsersListContext";
import AdminUsersListPage from "../admin_users/admin_users_list/pages/adminUsersListPage";
import { AdminRolesListProvider } from "../admin_roles_and_permissions/admin_roles_list/adminRolesListContext";
import AdminRolesListPage from "../admin_roles_and_permissions/admin_roles_list/pages/adminRolesListPage";
import ForbiddenPage from "../components/ForbiddenPage";
import LoginChangePassword from "../authentication/login_change_password";
import INECountriesPage from "../admin_ine/admin_ine_countries/pages/adminINECountriesPage";
import { INEProvider } from "../admin_ine/admin_ine_countries/adminINECountriesContext";

import { IneProvider } from "../ine/ineContext";
import InePage from "../ine/inePage";
import { IneConfigProvider } from "../ine/ine_config/ineConfigContext";
import IneConfigPage from "../ine/ine_config/pages/ineConfigPage";
import { InePartsProvider } from "../ine/ine_parts/inePartsContext";
import InePartsPage from "../ine/ine_parts/pages/inePartsPage";
import { IneBookingsProvider } from "../ine/ine_bookings/ineBookingsContext";
import IneBookingsPage from "../ine/ine_bookings/pages/ineBookingsPage";
import { IneRegistrersProvider } from "../ine/ine_registrer/ineRegistrersContext";
import IneRegistrersPage from "../ine/ine_registrer/pages/ineRegistrersPage";
import { InvoicingProvider } from "../invoicing/invoiceContext";
import InvoicingPage from "../invoicing/pages/invoice";

const HouseKeepingRoutes = () => {
  return (
    <CommonProvider>
      <AuthProvider>
        <Routes>
          <Route
            path="/login_change_password/:session"
            element={<LoginChangePassword />}
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />
          {/* App principal */}
          <Route element={<MainLayout />}>
            <Route
              path="/planification"
              element={
                <ProtectedRoute require={{ module: "planification" }}>
                  <PlanificationProvider>
                    <PlanificationPage />
                  </PlanificationProvider>
                </ProtectedRoute>
              }
            />

            <Route
              path="/rules"
              element={
                <ProtectedRoute require={{ module: "rules" }}>
                  <RuleProvider>
                    <RulesPage />
                  </RuleProvider>
                </ProtectedRoute>
              }
            />

            <Route path="/property">
              <Route
                index
                element={
                  <ProtectedRoute require={{ module: "property_config" }}>
                    <PropertyProvider>
                      <PropertyPage />
                    </PropertyProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="category/:categoryId"
                element={
                  <ProtectedRoute require={{ module: "property_config" }}>
                    <CategoryProvider>
                      <PropertyCategoryPage />
                    </CategoryProvider>
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route
              path="/settings"
              element={
                <ProtectedRoute require={{ module: "settings" }}>
                  <SettingsProvider>
                    <SettingsPage />
                  </SettingsProvider>
                </ProtectedRoute>
              }
            />

            <Route
              path="/invoicing"
              element={
                <ProtectedRoute require={{ module: "settings" }}>
                  {/* InvoicingProvider can be added here when needed */}
                  <InvoicingProvider>
                    <InvoicingPage />
                  </InvoicingProvider>
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory"
              element={
                <ProtectedRoute require={{ module: "inventory" }}>
                  <InventoryProvider>
                    <InventoryPage />
                  </InventoryProvider>
                </ProtectedRoute>
              }
            />

            <Route
              path="/blocks"
              element={
                <ProtectedRoute require={{ module: "blocks" }}>
                  <BlocksProvider>
                    <BlocksPage />
                  </BlocksProvider>
                </ProtectedRoute>
              }
            />

            {/* INE */}
            <Route
              path="/ine"
              element={
                <ProtectedRoute require={{ module: "ine" }}>
                  <IneProvider>
                    <InePage />
                  </IneProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="config" replace />} />
              <Route
                path="parts"
                element={
                  <InePartsProvider>
                    <InePartsPage />
                  </InePartsProvider>
                }
              />
              <Route
                path="registrers"
                element={
                  <IneRegistrersProvider>
                    <IneRegistrersPage />
                  </IneRegistrersProvider>
                }
              />
              <Route
                path="bookings"
                element={
                  <IneBookingsProvider>
                    <IneBookingsPage />
                  </IneBookingsProvider>
                }
              />
              <Route
                path="config"
                element={
                  <IneConfigProvider>
                    <IneConfigPage />
                  </IneConfigProvider>
                }
              />
            </Route>
          </Route>{" "}
          <Route element={<AdminLayout />}>
            <Route
              path="/admin/properties"
              element={
                <ProtectedRoute require={{ module: "admin_property" }}>
                  <AdminPropertiesListProvider>
                    <AdminPropertiesListPage />
                  </AdminPropertiesListProvider>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/properties/list"
              element={
                <ProtectedRoute require={{ module: "admin_property" }}>
                  <AdminPropertiesListProvider>
                    <AdminPropertiesListPage />
                  </AdminPropertiesListProvider>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute require={{ module: "admin_user" }}>
                  <AdminUsersListProvider>
                    <AdminUsersListPage />
                  </AdminUsersListProvider>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/roles"
              element={
                <ProtectedRoute require={{ module: "admin_roles" }}>
                  <AdminRolesListProvider>
                    <AdminRolesListPage />
                  </AdminRolesListProvider>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/ine/countries"
              element={
                <ProtectedRoute require={{ module: "admin_roles" }}>
                  <INEProvider>
                    <INECountriesPage />
                  </INEProvider>
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="/logout" element={<LogoutPage />} />
        </Routes>
      </AuthProvider>
    </CommonProvider>
  );
};

export default HouseKeepingRoutes;
